import { Box, Button, Typography } from '@mui/material'
import { Close } from '@mui/icons-material'
import React, { useMemo, useRef, useState } from 'react'
import { useStyles } from '../style'
import { SearchField, CustomSkillInput, CategoryGroup } from '../components'
import ConfirmDialog from '../../confirm-dialog'
import useFormFactor from '../../../hooks/useFormFactor'

const SkillsPage = ({
  title,
  subtitle,
  searchTerm,
  onSearchChange,
  groupedOptions = [],
  selectedSkills,
  onToggleSkill,
  otherTopics,
  onAddOtherTopic,
  onRemoveOtherTopic,
  allAvailableOptions,
  onSelectExistingSkill,
  onClearSkills
}) => {
  const classes = useStyles()
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  const selectedCount = selectedSkills.length
  const { isMobile, isDesktop } = useFormFactor()
  // Smaller screens fit fewer chips per row, so preview more rows there:
  // phones = 4 rows, tablets = 2 rows, desktop = 1 row.
  const previewRows = isMobile ? 4 : isDesktop ? 1 : 2

  const term = (searchTerm || '').trim().toLowerCase()
  const isSearching = term.length > 0

  const availableSkillsSelected = selectedSkills.filter(skillSk =>
    allAvailableOptions.some(option => option.sk === skillSk)
  ).length

  const filteredGroups = useMemo(() => {
    if (!isSearching) return groupedOptions

    return groupedOptions
      .map(group => {
        const groupNameMatch = group.parentName.toLowerCase().includes(term)
        const children = groupNameMatch
          ? group.children
          : group.children.filter(child => child.name.toLowerCase().includes(term))
        return { ...group, children }
      })
      .filter(group => group.children.length > 0)
  }, [groupedOptions, term, isSearching])

  const gridRef = useRef(null)
  const [activeSk, setActiveSk] = useState(null)

  // All chips across the filtered groups, in render order. Used to pick the
  // single roving tab stop, keyed by stable `sk` (not a positional index) so
  // per-group "See more" clamping can't desync it.
  const visibleSkills = useMemo(() => {
    return filteredGroups.flatMap(group => group.children)
  }, [filteredGroups])

  // Exactly one chip is tabbable. Prefer the last-focused chip; if it has been
  // filtered out, fall back to the first chip (the first group always renders
  // at least one).
  const effectiveActiveSk = useMemo(() => {
    if (activeSk && visibleSkills.some(skill => skill.sk === activeSk)) return activeSk
    return visibleSkills[0]?.sk ?? null
  }, [activeSk, visibleSkills])

  // Roving focus across the *rendered* chips. Activation (Enter/Space) is
  // handled natively by each chip's MUI ButtonBase, so we only move focus on
  // arrows. Keys from the search / custom-topic inputs are ignored because the
  // event target is not a skill chip.
  const handleGridKeyDown = (e) => {
    const chip = e.target.closest('[data-test^="skill-chip-"]')
    if (!chip || !gridRef.current) return

    const arrowKeys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp']
    if (!arrowKeys.includes(e.key)) return

    e.preventDefault()
    const chips = Array.from(gridRef.current.querySelectorAll('[data-test^="skill-chip-"]'))
    const current = chips.indexOf(chip)
    if (current === -1) return

    const forward = e.key === 'ArrowRight' || e.key === 'ArrowDown'
    const next = forward
      ? Math.min(current + 1, chips.length - 1)
      : Math.max(current - 1, 0)
    if (next !== current) chips[next].focus()
  }

  return (
    <Box className={classes.content} data-test='topics-page'>
      <Typography
        id='lpq-dialog-title'
        className={classes.question}
        data-test='topics-page-title'
        component='h2'
      >
        {title || 'What topics would you like to develop?'}
      </Typography>
      <Typography
        id='lpq-dialog-description'
        className={classes.subtitle}
        data-test='topics-page-subtitle'
      >
        {subtitle || 'Tell us a bit about yourself so we can help you get the most out of Mindtools.'}
      </Typography>

      <SearchField
        value={searchTerm}
        onChange={e => onSearchChange(e.target.value)}
        placeholder='Search skills...'
      />

      <Box
        ref={gridRef}
        className={classes.topicsGroups}
        data-test='topics-grid'
        role='group'
        aria-label='Skill and topic selection options'
        onKeyDown={handleGridKeyDown}
      >
        {filteredGroups.length === 0
          ? (
            <Typography className={classes.subtitle} data-test='topics-no-results'>
              No matching skills.
            </Typography>
            )
          : (
              filteredGroups.map(group => (
                <CategoryGroup
                  key={group.parentSk || group.parentName}
                  group={group}
                  selectedSkills={selectedSkills}
                  onToggleSkill={onToggleSkill}
                  previewRows={previewRows}
                  forceExpanded={isSearching}
                  activeSk={effectiveActiveSk}
                  onChipFocus={setActiveSk}
                />
              ))
            )}
      </Box>

      <Box className={classes.page2Footer} data-test='topics-page-footer'>
        <Box className={classes.selectionSummaryRow}>
          <Typography className={classes.selectionCount} data-test='topics-selection-count'>
            {availableSkillsSelected} of {allAvailableOptions.length} topics selected
          </Typography>
          <Button
            variant='outlined'
            className={classes.clearAllButton}
            startIcon={<Close fontSize='small' />}
            onClick={() => setShowClearConfirm(true)}
            disabled={selectedCount === 0}
            data-test='topics-clear-button'
          >
            Clear all ({selectedCount})
          </Button>
        </Box>
        <CustomSkillInput
          onAddSkill={onAddOtherTopic}
          customTopics={otherTopics.map(topic => ({ name: topic, sk: `custom_${topic}` }))}
          onRemoveSkill={(sk) => onRemoveOtherTopic(sk.replace('custom_', ''))}
          allAvailableOptions={allAvailableOptions}
          onSelectExistingSkill={onSelectExistingSkill}
        />
      </Box>

      <ConfirmDialog
        name='clear-all-topics'
        title='Clear all topics?'
        description={`This will remove all ${selectedCount} selected topics. You can select them again at any time.`}
        open={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={onClearSkills}
        cancelText='Cancel'
        confirmText='Clear all'
        variant='warning'
      />
    </Box>
  )
}

export default SkillsPage
